import {
  addDefaultProps,
  addDisplayName,
  addHandlers,
  addProps,
  addState,
  flowMax,
} from 'ad-hok'
import cx from 'classnames'
import { JssStyle } from 'jss'
import { ChangeEvent, FC } from 'react'
import { createUseStyles } from 'react-jss'

import { sansSerifFontFamily } from 'utils/styles'
import { INPUT_NAME_Z_INDEX } from 'utils/zIndex'
import hide from '../assets/hide.svg'
import show from '../assets/show.svg'

const styles = {
  label: cx(
    'absolute',
    'text-lg',
    'text-rodeo-grey/96',
    'duration-100',
    'transform',
    '-translate-y-4',
    'scale-[.65]',
    'top-4',
    'z-10', // TODO: replace this with INPUT_NAME_Z_INDEX when de-Tailwind-ify'ing
    'origin-[0]',
    'left-2.5',
    'peer-focus:scale-[.65]',
    'peer-focus:-translate-y-4',
    'peer-focus:leading-label',
    'peer-focus:tracking-wide',
    'peer-placeholder-shown:scale-100',
    'peer-placeholder-shown:translate-y-0',
    'peer-disabled:-translate-y-4',
    'peer-disabled:leading-label',
    'peer-disabled:tracking-wide',
    'peer-disabled:scale-[.65]',
    'pointer-events-none'
  ),
  labelError: cx('text-rodeo-errors'),
  input: cx(
    'relative',
    'px-2.5',
    'pb-2.5',
    'w-full',
    'text-sm',
    'text-rodeo-grey/96',
    'appearance-none',
    'focus:outline-none',
    'focus:ring-0',
    'focus:border-rodeo-secondary1/56',
    'peer',
    'rounded-sm',
    'border-rodeo-secondary1/24',
    'bg-rodeo-grey/[.02]',
    'focus:bg-rodeo-grey/[.04]',
    'caret-rodeo-secondary1',
    'disabled:bg-rodeo-input-bg/[.08]'
  ),
  inputError: cx(
    'bg-rodeo-errors/[.04]',
    'border-rodeo-errors/24',
    'focus:bg-rodeo-errors/[.04]',
    'focus:border-rodeo-errors/56'
  ),
  area: cx('resize-y'),
  clearButton: cx('absolute', 'right-2.5', 'min-h-full'),
  clearButtonIcon: cx(
    'text-rodeo-secondary1/56',
    'hover:text-rodeo-secondary1/96'
  ),
  clearButtonErrorIcon: cx(
    'text-rodeo-errors/56',
    'hover:text-rodeo-errors1/96'
  ),
  hasLabel: 'pt-4',
}

const INPUT_LEFT_PADDING = 16
const INPUT_HEIGHT = 64

const labelShrunkStyle: JssStyle = {
  transform: 'translateY(-5px) scale(0.75)',
  transformOrigin: 'top left',
}

const useStyles = createUseStyles({
  inputWrapper: {
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    height: INPUT_HEIGHT,
  },
  input: {
    height: INPUT_HEIGHT,
    paddingLeft: INPUT_LEFT_PADDING,
    paddingRight: 40,
    paddingTop: 29.5,
    color: 'rgba(255, 255, 255, 0.8)',
    fontFamily: sansSerifFontFamily,
    fontSize: 16,
    fontWeight: 500,
    lineHeight: '26px',
    letterSpacing: 0.2,
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
    border: '1px solid rgba(255, 255, 255, 0.24)',
    borderRadius: 2,
    caretColor: '#90734F',
    '&:focus': {
      backgroundColor: 'rgba(255, 255, 255, 0.02)',
      borderColor: 'rgba(255, 255, 255, 0.24)',
      outline: 'none',
    },
  },
  textarea: {
    resize: 'vertical',
    height: 'auto',
  },
  inputError: {
    // force this to prevail vs inputClassName
    'html &': {
      backgroundColor: 'rgba(245, 117, 97, 0.08)',
      borderColor: 'rgba(245, 117, 97, 0.24)',
      '&:focus': {
        backgroundColor: 'rgba(245, 117, 97, 0.08)',
        borderColor: 'rgba(245, 117, 97, 0.56)',
      },
    },
  },
  label: {
    position: 'absolute',
    left: INPUT_LEFT_PADDING,
    top: 0,
    height: INPUT_HEIGHT,
    display: 'flex',
    alignItems: 'center',
    zIndex: INPUT_NAME_Z_INDEX,
    pointerEvents: 'none',
    color: 'rgba(255, 255, 255, 0.8)',
    transitionDuration: '100ms',
    'input:focus + &': labelShrunkStyle,
    'textarea:focus + &': labelShrunkStyle,
  },
  labelShrunk: labelShrunkStyle,
  labelError: {
    color: '#F57561',
  },
  eyeButtonContainer: {
    position: 'absolute',
    right: 2,
    top: 0,
    height: INPUT_HEIGHT,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    paddingLeft: 14,
    paddingRight: 14,
    cursor: 'pointer',
  },
  eyeButton: {
    color: 'rgba(255, 255, 255, 0.8)',
    '&:hover': {
      color: 'rgba(144, 115, 79, 0.96)',
    },
  },
  eyeButtonError: {
    color: 'rgba(245, 117, 97, 0.56)',
    '&:hover': {
      color: 'rgba(245, 117, 97, 0.96)',
    },
  },
})

interface Props {
  id?: string
  label?: string
  value?: string
  placeholder?: string
  onChange: (value: string) => void
  onKeyPress?: (event: React.KeyboardEvent<HTMLInputElement>) => void
  type?: 'text' | 'password'
  error?: string
}

export const TextField: FC<Props> = flowMax(
  addDisplayName('TextField'),
  addDefaultProps(({ type }) => ({
    value: '',
    placeholder: '',
    showVisibilityToggle: type === 'password',
  })),
  addState('type', 'setType', ({ type }) => type ?? 'text'),
  addHandlers({
    onChange:
      ({ onChange }) =>
      ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
        onChange(value)
      },
    onToggleVisibility:
      ({ type, setType }) =>
      () => {
        setType(type === 'password' ? 'text' : 'password')
      },
  }),
  addProps(() => ({
    classes: useStyles(),
  })),
  ({
    id,
    value,
    type,
    label,
    error,
    onChange,
    showVisibilityToggle,
    onToggleVisibility,
    onKeyPress,
    classes,
  }) => (
    <div className={classes.inputWrapper}>
      <input
        id={id}
        type={type}
        value={value}
        onChange={onChange}
        onKeyUp={onKeyPress}
        className={cx(classes.input, 'focus:ring-0', styles.input)}
        spellCheck="false"
        autoCorrect="off"
        autoCapitalize="off"
        autoComplete="off"
      />
      {!!label && (
        <label
          htmlFor={id}
          className={cx(classes.label, !!value && classes.labelShrunk)}
        >
          {label}
        </label>
      )}
      {showVisibilityToggle && (
        <div className={classes.eyeButtonContainer}>
          <img
            src={type === 'password' ? show : hide}
            onClick={onToggleVisibility}
            alt="togglePasswordVisibility"
            className={error ? classes.eyeButtonError : classes.eyeButton}
          />
        </div>
      )}
    </div>
  )
)
